import React from "react";
import { CheckCircle2 } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export const Field = ({ label, children }) => (
  <div>
    <Label className="text-slate-300 text-[12.5px]">{label}</Label>
    <div className="mt-2">{children}</div>
  </div>
);

export const CountryButton = ({ active, onClick, flag, name, sub }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left rounded-2xl border p-5 transition-colors duration-200 ${
      active
        ? "border-amber-400 bg-amber-500/10"
        : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
    }`}
  >
    <div className="flex items-center gap-2 text-white font-semibold">
      <span className="text-xl">{flag}</span> {name}
    </div>
    <p className="mt-1.5 text-slate-300 text-[13px]">{sub}</p>
  </button>
);

export const Pill = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full border px-4 py-2 text-[13.5px] transition-colors ${
      active
        ? "border-amber-400 bg-amber-500/15 text-amber-200"
        : "border-white/10 bg-white/[0.02] text-slate-200 hover:bg-white/[0.05]"
    }`}
  >
    {children}
  </button>
);

export const StepBasics = ({ data, setField }) => (
  <div className="mt-7">
    <h3 className="text-white text-[20px] font-semibold">Let's start with the basics</h3>
    <p className="mt-1.5 text-slate-400 text-[14px]">So our counsellor can reach out on WhatsApp.</p>
    <div className="mt-6 grid sm:grid-cols-2 gap-4">
      <Field label="Full Name *">
        <Input value={data.name} onChange={setField("name")} placeholder="e.g. Priya Sharma" className="cc-input" />
      </Field>
      <Field label="Email *">
        <Input type="email" value={data.email} onChange={setField("email")} placeholder="you@email.com" className="cc-input" />
      </Field>
      <Field label="WhatsApp Number *">
        <Input value={data.whatsapp} onChange={setField("whatsapp")} placeholder="+91 98xxxxxxxx" className="cc-input" />
      </Field>
      <Field label="City *">
        <Input value={data.city} onChange={setField("city")} placeholder="Ahmedabad, Mumbai, ..." className="cc-input" />
      </Field>
    </div>
  </div>
);

const DESTINATIONS = [
  { flag: "\uD83C\uDDE6\uD83C\uDDFA", name: "Australia", sub: "PR pathways" },
  { flag: "\uD83C\uDDEC\uD83C\uDDE7", name: "United Kingdom", sub: "1–2 yr degrees" },
  { flag: "\uD83C\uDDF3\uD83C\uDDFF", name: "New Zealand", sub: "Safe & welcoming" },
];

export const StepDestination = ({ data, setField }) => (
  <div className="mt-7">
    <h3 className="text-white text-[20px] font-semibold">Which destination are you aiming for?</h3>
    <p className="mt-1.5 text-slate-400 text-[14px]">Pick one — you can change later with your counsellor.</p>
    <div className="mt-6 grid sm:grid-cols-3 gap-3">
      {DESTINATIONS.map((d) => (
        <CountryButton
          key={d.name}
          flag={d.flag}
          name={d.name}
          sub={d.sub}
          active={data.destination === d.name}
          onClick={() => setField("destination")(d.name)}
        />
      ))}
    </div>
  </div>
);

const EXAMS = ["IELTS", "PTE", "TOEFL", "LanguageCert", "Not yet taken"];

export const StepExam = ({ data, setField }) => (
  <div className="mt-7">
    <h3 className="text-white text-[20px] font-semibold">English exam status</h3>
    <p className="mt-1.5 text-slate-400 text-[14px]">Which exam have you taken (or plan to take)?</p>
    <div className="mt-6 flex flex-wrap gap-2.5">
      {EXAMS.map((opt) => (
        <Pill key={opt} active={data.exam === opt} onClick={() => setField("exam")(opt)}>{opt}</Pill>
      ))}
    </div>
    {data.exam && data.exam !== "Not yet taken" && (
      <div className="mt-5">
        <Field label="Latest score (optional)">
          <Input value={data.score} onChange={setField("score")} placeholder="e.g. IELTS 7.0 / PTE 65 / TOEFL 95" className="cc-input" />
        </Field>
      </div>
    )}
  </div>
);

const YEARS = ["2026", "2027", "2028"];
const SEASONS = ["Feb / Spring", "Jul / Summer", "Sep / Autumn"];
// Only future intakes: for 2026 we hide Feb (already past); 2027+ all seasons available
const isSeasonAvailable = (year, season) => {
  if (year === "2026" && season === "Feb / Spring") return false;
  return true;
};

export const StepIntake = ({ data, setField }) => (
  <div className="mt-7">
    <h3 className="text-white text-[20px] font-semibold">When do you want to start?</h3>
    <p className="mt-1.5 text-slate-400 text-[14px]">Pick your preferred intake (July 2026 onward).</p>
    <div className="mt-6">
      <div className="text-slate-300 text-[13px] mb-2">Year</div>
      <div className="flex flex-wrap gap-2.5">
        {YEARS.map((y) => (
          <Pill
            key={y}
            active={data.intakeYear === y}
            onClick={() => {
              setField("intakeYear")(y);
              // If switching to 2026 and an unavailable season is selected, reset it
              if (y === "2026" && data.intakeSeason && !isSeasonAvailable(y, data.intakeSeason)) {
                setField("intakeSeason")("");
              }
            }}
          >
            {y}
          </Pill>
        ))}
      </div>
    </div>
    <div className="mt-5">
      <div className="text-slate-300 text-[13px] mb-2">Season</div>
      <div className="flex flex-wrap gap-2.5">
        {SEASONS.map((s) => {
          const disabled = data.intakeYear ? !isSeasonAvailable(data.intakeYear, s) : false;
          if (disabled) {
            return (
              <button
                key={s}
                type="button"
                disabled
                title="Past intake — pick a future year"
                className="rounded-full border border-white/5 bg-white/[0.01] text-slate-600 px-4 py-2 text-[13.5px] line-through cursor-not-allowed"
              >
                {s}
              </button>
            );
          }
          return (
            <Pill key={s} active={data.intakeSeason === s} onClick={() => setField("intakeSeason")(s)}>
              {s}
            </Pill>
          );
        })}
      </div>
    </div>
  </div>
);

export const StepNotes = ({ data, setField }) => (
  <div className="mt-7">
    <h3 className="text-white text-[20px] font-semibold">Almost done. Anything we should know?</h3>
    <p className="mt-1.5 text-slate-400 text-[14px]">Course preference, budget, or questions — optional.</p>
    <div className="mt-6">
      <Field label="Notes (optional)">
        <textarea
          rows={4}
          value={data.notes}
          onChange={setField("notes")}
          placeholder="e.g. Looking at MS Data Science, budget under AUD 40k/yr"
          className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-3.5 py-2.5 text-[14.5px] text-white placeholder:text-slate-500 focus:border-amber-400 focus:ring-2 focus:ring-amber-500/20"
        />
      </Field>
    </div>
    <div className="mt-5 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] p-4 text-emerald-200 text-[13.5px] flex items-start gap-2.5">
      <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5" />
      By submitting you agree to be contacted by Competitive Careers Pvt Ltd on WhatsApp / email. No spam.
    </div>
  </div>
);
